<template>
  <div
    id="cumulative-graph"
    class="relative flex justify-center w-full py-6 bg-slate-200"
  >
    <div
      class="w-full max-w-5xl border bg-pure-white rounded-3xl border-slate-400"
    >
      <div
        id="graph-display"
        class="flex flex-col gap-8 p-6 border-b lg:p-10 border-slate-400"
      >
        <div class="flex flex-col gap-1">
          <BlTextTitle4 text="Cumulative Graph" />
          <BlTextBody2
            text="With the cumulative graph, you can easily track the progress of the mastered targets over time."
          />
        </div>
        <div class="flex items-center gap-4">
          <BlInputFilter
            class="w-40"
            :input="filterInput"
            @changed="onChangeDate"
          />
          <BlInputSelect
            v-if="totalTargetInGraph"
            :input="graphTypeInput"
            @changed="onChangeFilter"
            class="max-w-56"
          />
          <BlInputSelect
            v-if="totalTargetInGraph"
            :input="progressDisplayInput"
            @changed="onChangeFilter"
            class="max-w-72"
          />
        </div>
        <div v-if="graphLoading" class="grid w-full h-128 place-items-center">
          <div class="blum-loader"></div>
        </div>
        <div
          v-else-if="!totalTargetInGraph"
          class="grid w-full text-center h-128 place-items-center"
        >
          <BlTextBody1
            v-if="activeFilterDate === 'Filter by date'"
            text="The cumulative graph will be displayed once a target has been mastered."
            class="text-grey-400"
          />
          <BlTextBody1
            v-else
            text="Currently, there is no data to display on the cumulative graph for the selected date, as no new targets have been mastered during this period."
            class="text-center text-grey-400 px-36"
          />
        </div>
        <GraphPreview
          v-else
          :graphData="cumulativeGraph"
          :graphType="graphTypeInput.model.value"
          :progressDisplay="progressDisplayInput.model.value"
        />
      </div>
      <div id="detail-target" class="flex flex-col gap-4 px-10 pt-5 pb-10">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <BlTextSubtitle1 text="Detail of mastered targets" />
            <BlBubbleCount :count="totalTargetInTable || 0" />
          </div>
          <div class="flex items-center gap-4">
            <BlInputSelect :input="groupByInput" @changed="onChangeSort" />
            <BlInputSelect
              v-if="groupByInput.model.value === 'date'"
              :input="sortByInput"
              @changed="onChangeSort"
            />
          </div>
        </div>
        <div v-if="tableLoading" class="grid w-full h-128 place-items-center">
          <div class="blum-loader"></div>
        </div>
        <div
          v-else-if="!totalTargetInTable"
          class="grid w-full text-center h-128 place-items-center"
        >
          <BlTextBody1
            text="Currently, the client doesn't have any targets yet."
            class="text-grey-400"
          />
        </div>
        <TablePreview v-else :groupBy="groupByInput.model.value" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

import GraphPreview from "./cumulativeGraph/GraphPreview.vue";
import TablePreview from "./cumulativeGraph/TablePreview.vue";
//
import BlInputFilter from "../BlInputFilter.vue";
import BlInputSelect from "../BlInputSelect.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlTextTitle4 from "../BlTextTitle4.vue";
import BlTextSubtitle1 from "../BlTextSubtitle1.vue";
import BlBubbleCount from "../BlBubbleCount.vue";
import BlTextSubtitle2 from "../BlTextSubtitle2.vue";
import BlTextBody1 from "../BlTextBody1.vue";

export default {
  name: "ClientCumulativeGraph",
  components: {
    GraphPreview,
    TablePreview,
    //
    BlTextTitle4,
    BlTextBody2,
    BlInputFilter,
    BlInputSelect,
    BlTextSubtitle1,
    BlBubbleCount,
    BlTextSubtitle2,
    BlTextBody1
  },
  props: ["id"],
  data() {
    const defaultFilter = [{ start_date: "", end_date: "" }, []];
    return {
      graphLoading: false,
      tableLoading: false,
      defaultFilter,
      filterInput: {
        model: defaultFilter,
        tabs: [
          { name: "Date", kind: "date" },
          {
            name: "Curriculum",
            kind: "multi",
            options: [],
            url: `/api/v1/curriculums?sort=name_asc&query=`,
            fetchPage: 1,
            dataKey: "curriculums",
            countKey: "total_count",
            optionKeys: { value: "id", label: "name", prefixColor: "color" }
          }
        ]
      },
      graphTypeInput: {
        model: { name: "Line", value: "line" },
        prefix: "Graph type",
        combine: true,
        options: [
          { name: "Line", value: "line" },
          { name: "Stacked", value: "stacked" }
        ]
      },
      progressDisplayInput: {
        model: { name: "Per day", value: "date" },
        prefix: "Progress display",
        combine: true,
        options: [
          { name: "Per month", value: "month" },
          { name: "Per day", value: "date" }
        ]
      },
      groupByInput: {
        model: { name: "Curriculum", value: "curriculum" },
        prefix: "Group by",
        combine: true,
        disabled: false,
        options: [
          { name: "Curriculum", value: "curriculum" },
          { name: "Date mastered", value: "date" }
        ]
      },
      sortByInput: {
        model: { name: "Oldest", value: "oldest" },
        prefix: "Sort by",
        combine: true,
        options: [
          { name: "Oldest", value: "oldest" },
          { name: "Most recent", value: "newest" }
        ]
      }
    };
  },
  computed: {
    ...mapGetters(["cumulativeGraph", "cumulativeTable"]),
    activeFilterDate() {
      const {
        start_date: startDate,
        end_date: endDate
      } = this.filterInput.model[0];
      if (!startDate || !endDate) return `Filter by date`;
      const format = "DD/MM/YYYY";
      return `Date ${moment(startDate).format(format)} - ${moment(
        endDate
      ).format(format)}`;
    },
    getParams() {
      let params = "";
      const {
        start_date: startDate,
        end_date: endDate
      } = this.filterInput.model[0];
      const curriculumIds = this.filterInput.model[1]
        .map(i => i.value)
        .join(",");
      // graph
      const graphType = this.graphTypeInput.model.value;
      const progressDisplay = this.progressDisplayInput.model.value;
      // table
      const groupByTable = this.groupByInput.model.value;
      const sortByTable = this.sortByInput.model.value;

      if (startDate) {
        params += `&start_date=${moment(startDate).format("YYYY-MM-DD")}`;
      }
      if (endDate) {
        params += `&end_date=${moment(endDate).format("YYYY-MM-DD")}`;
      }
      if (curriculumIds) {
        params += `&curriculum_id=${curriculumIds}`;
      }

      if (graphType) {
        params += `&graph_type=${graphType}`;
      }
      if (progressDisplay) {
        params += `&graph_group_by=${progressDisplay}`;
      }
      if (groupByTable) {
        params += `&table_group_by=${groupByTable}`;
        if (groupByTable === "date" && sortByTable) {
          params += `&sort=${sortByTable}`;
        }
      }
      return params;
    },
    totalTargetInGraph() {
      let total = 0;
      this.cumulativeGraph.datasets.map(i => {
        total += i.data.reduce((partialSum, a) => partialSum + a, 0);
      });
      return total;
    },
    totalTargetInTable() {
      let total = 0;
      this.cumulativeTable.forEach(i => {
        total += i.number_of_target;
      });
      return total;
    }
  },
  watch: {
    cumulativeTable: {
      deep: true,
      handler(val) {
        let total = 0;
        val.forEach(i => {
          total += i.number_of_mastered;
        });
        if (!total) {
          this.groupByInput.disabled = true;
          if (this.groupByInput.model.value === "date") {
            this.groupByInput.model = {
              name: "Curriculum",
              value: "curriculum"
            };
            this.fetchCumulativeTable();
          }
        } else {
          this.groupByInput.disabled = false;
        }
      }
    }
  },
  mounted() {
    this.graphLoading = true;
    this.tableLoading = true;
    const query = this.$route.query;

    if (query.start_date || query.end_date) {
      this.filterInput.model[0] = {
        start_date: query.start_date || "",
        end_date: query.end_date || ""
      };
    }

    if (query.curriculum_id) {
      this.filterInput.model[1] = query.curriculum_id.split(",");
    }

    if (query.graph_type) {
      const foundType = this.graphTypeInput.options.find(
        i => i.value === query.graph_type
      );
      this.graphTypeInput.model = foundType;
    }

    if (query.graph_group_by) {
      const foundDisplay = this.progressDisplayInput.options.find(
        i => i.value === query.graph_group_by
      );
      this.progressDisplayInput.model = foundDisplay;
    }

    if (query.table_group_by) {
      const foundGroup = this.groupByInput.options.find(
        i => i.value === query.table_group_by
      );
      this.groupByInput.model = foundGroup;

      if (query["sort"]) {
        const foundSort = this.sortByInput.options.find(
          i => i.value === query["sort"]
        );
        this.sortByInput.model = foundSort;
      }
    }

    this.fetchCumulativeGraph();
    this.fetchCumulativeTable();
  },
  methods: {
    ...mapActions(["getCumulative"]),
    onChangeDate(payload) {
      payload.input.model = payload.value;
      this.graphLoading = true;
      this.tableLoading = true;
      this.fetchCumulativeGraph();
      this.fetchCumulativeTable();
      this.syncPath();
    },
    onChangeFilter(payload) {
      payload.input.model = payload.value;
      this.graphLoading = true;
      this.fetchCumulativeGraph();
      this.syncPath();
    },
    onChangeSort(payload) {
      payload.input.model = payload.value;
      this.tableLoading = true;
      this.fetchCumulativeTable();
      this.syncPath();
    },
    syncPath() {
      // sync url path for this component
      const basePath = `/clients/${this.id}/cumulative-graph?view`;
      const incomingPath = basePath + this.getParams;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    async fetchCumulativeGraph() {
      const suffix = this.getParams
        .replace("graph_group_by", "group_by")
        // remove unused params
        .replace("table_group_by=", "")
        .replace("sort=", "");
      const params = `?view=graph${suffix}`;
      await this.getCumulative({ client_id: this.id, params });
      this.graphLoading = false;
    },
    async fetchCumulativeTable() {
      const suffix = this.getParams
        .replace("table_group_by", "group_by")
        // remove unused params
        .replace("graph_type=", "")
        .replace("graph_group_by=", "");
      const params = `?view=table${suffix}`;
      await this.getCumulative({ client_id: this.id, params });
      this.tableLoading = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/ClientCumulativeGraph.vue